import React, { useState, useEffect } from 'react';
import "../../css/Payment.css";
import { Search, User, Download, CreditCard, Printer, CheckCircle, X } from 'lucide-react';
import { jsPDF } from 'jspdf';
import Logo from '../../assets/logonew.jpeg';
import Loader from "../../components/Loader";
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

function Payment() {
    const [searchQuery, setSearchQuery] = useState('');
    const [selectedStudent, setSelectedStudent] = useState(null);
    const [selectedFeeTypes, setSelectedFeeTypes] = useState([]);
    const [paymentMethod, setPaymentMethod] = useState('');
    const [showPreview, setShowPreview] = useState(false);
    const [paymentSuccess, setPaymentSuccess] = useState(false);
    const [accountDetails, setAccountDetails] = useState(null);
    const [paymentAmount, setPaymentAmount] = useState({});
    const [receiptData, setReceiptData] = useState(null);
    const [students, setStudents] = useState([]);
    const [filteredStudents, setFilteredStudents] = useState([]);
    const [isLoading, setIsLoading] = useState(false);

    // Fee types data - we'll keep this static for now
    const feeTypes = [
        { id: 1, type: 'Admission Fee', defaultAmount: 5000 },
        { id: 2, type: 'Tuition Fee', defaultAmount: 30000 },
        { id: 3, type: 'Hostel Fee', defaultAmount: 20000 },
        { id: 4, type: 'Food Fee', defaultAmount: 15000 },
        { id: 5, type: 'Library Fee', defaultAmount: 2000 },
    ];

    // Payment methods - we'll keep this static
    const paymentMethods = [
        {
            id: 1,
            name: 'UPI',
            icon: 'creditCard',
            accountInfo: {
                upiId: 'institution@upi',
                qrCode: '/api/placeholder/200/200',
                instructions: 'Scan QR code or use UPI ID to make payment'
            }
        },
        {
            id: 2,
            name: 'Bank Transfer',
            icon: 'creditCard',
            accountInfo: {
                accountNumber: '1234567890',
                ifscCode: 'BANK0001234',
                accountName: 'Uniums School pro',
                bankName: 'National Bank',
                instructions: 'Please use your Roll Number as reference'
            }
        },
        {
            id: 3,
            name: 'Cash',
            icon: 'creditCard',
            accountInfo: {
                location: 'Fee Counter, Admin Building',
                timings: '10:00 AM - 3:00 PM (Mon-Fri)',
                instructions: 'Please collect receipt after payment'
            }
        }
    ];

    // Fetch students data when component mounts
    useEffect(() => {
        fetchStudents();
    }, []);

    // Function to fetch students from API
    const fetchStudents = async () => {
        const token = localStorage.getItem("token");
        if (!token) {
            toast.error("Unauthorized! Please log in again.");
            return;
        }

        setIsLoading(true);

        try {
            const response = await fetch("https://schoolpro-api.uniums.com/api/students", {
                headers: {
                    "Authorization": `Bearer ${token}`
                }
            });

            if (!response.ok) {
                const errorData = await response.json();
                throw new Error(errorData.message || "Failed to fetch students");
            }

            const data = await response.json();
            setStudents(data);
            setFilteredStudents(data);
        } catch (error) {
            console.error("Error fetching students:", error);
            toast.error(`Error: ${error.message}`);
        } finally {
            setIsLoading(false);
        }
    };

    // Update filtered students whenever search query changes
    useEffect(() => {
        if (searchQuery) {
            const filtered = students.filter(student =>
                (student.firstName + ' ' + student.lastName).toLowerCase().includes(searchQuery.toLowerCase()) ||
                student.rollNumber.toLowerCase().includes(searchQuery.toLowerCase())
            );
            setFilteredStudents(filtered);
        } else {
            setFilteredStudents(students);
        }
    }, [searchQuery, students]);

    // Handle fee type selection
    const handleFeeTypeSelect = (feeType) => {
        if (selectedFeeTypes.find(fee => fee.id === feeType.id)) {
            setSelectedFeeTypes(selectedFeeTypes.filter(fee => fee.id !== feeType.id));
            const newAmount = { ...paymentAmount };
            delete newAmount[feeType.id];
            setPaymentAmount(newAmount);
        } else {
            setSelectedFeeTypes([...selectedFeeTypes, feeType]);
            setPaymentAmount({ ...paymentAmount, [feeType.id]: feeType.defaultAmount });
        }
    };

    // Calculate total amount
    const calculateTotal = () => {
        return Object.values(paymentAmount).reduce((total, amount) => total + Number(amount), 0);
    };

    // Handle payment amount change
    const handleAmountChange = (feeId, amount) => {
        setPaymentAmount({ ...paymentAmount, [feeId]: amount });
    };

    // Handle payment method selection
    const handlePaymentMethodSelect = (method) => {
        setPaymentMethod(method.name);
        setAccountDetails(method.accountInfo);
    };

    // Handle generate receipt
    const handleGenerateReceipt = () => {
        const receipt = {
            student: selectedStudent,
            feeTypes: selectedFeeTypes.map(fee => ({
                ...fee,
                amount: paymentAmount[fee.id]
            })),
            paymentMethod,
            paymentDetails: accountDetails,
            total: calculateTotal(),
            date: new Date().toLocaleDateString(),
            transactionId: 'TXN' + Math.floor(Math.random() * 1000000)
        };

        setReceiptData(receipt);
        setShowPreview(true);
    };

    // Handle download receipt as PDF and upload to Cloudinary
    const handleDownloadReceipt = async () => {
        // Create PDF document
        const doc = new jsPDF();

        // Add content to PDF
        doc.setFontSize(20);
        doc.text("UniUms School Pro", 105, 20, { align: "center" });

        doc.setFontSize(12);
        doc.text("Address Betnoti, Mayurbhanj, Odisha, Address Line 2", 105, 30, { align: "center" });
        doc.text("Phone: 123-456-7890 | Email: info@institution.edu", 105, 35, { align: "center" });

        doc.setFontSize(16);
        doc.text("PAYMENT RECEIPT", 105, 45, { align: "center" });
        doc.setFontSize(10);
        doc.text(`Receipt No: ${receiptData.transactionId}`, 20, 55);
        doc.text(`Date: ${receiptData.date}`, 150, 55);

        // Student details
        doc.setFontSize(12);
        doc.text("Student Details", 20, 65);
        doc.setFontSize(10);
        doc.text(`Name: ${receiptData.student.firstName} ${receiptData.student.lastName}`, 20, 75);
        doc.text(`Roll No: ${receiptData.student.rollNumber}`, 20, 80);
        doc.text(`Grade: ${receiptData.student.grade} - ${receiptData.student.section}`, 100, 75);
        doc.text(`Admission Date: ${new Date(receiptData.student.admissionDate).toLocaleDateString()}`, 100, 80);

        // Fee details
        doc.setFontSize(12);
        doc.text("Fee Details", 20, 90);

        // Table headers
        doc.setFontSize(10);
        doc.text("No.", 20, 100);
        doc.text("Description", 40, 100);
        doc.text("Amount (₹)", 150, 100);

        doc.line(20, 102, 190, 102); // Line below header

        // Table rows
        let yPos = 110;
        receiptData.feeTypes.forEach((fee, index) => {
            doc.text(`${index + 1}`, 20, yPos);
            doc.text(fee.type, 40, yPos);
            doc.text(`${fee.amount}`, 150, yPos);
            yPos += 7;
        });

        doc.line(20, yPos, 190, yPos);

        // Total
        doc.text("Total", 100, yPos + 7);
        doc.text(`₹${receiptData.total}`, 150, yPos + 7);

        // Payment details
        doc.setFontSize(12);
        doc.text("Payment Details", 20, yPos + 17);
        doc.setFontSize(10);
        doc.text(`Payment Method: ${receiptData.paymentMethod}`, 20, yPos + 25);
        doc.text(`Transaction ID: ${receiptData.transactionId}`, 20, yPos + 32);
        doc.text(`Payment Status: Success`, 20, yPos + 39);

        // Footer
        doc.setFontSize(8);
        doc.text("This is a computer-generated receipt and does not require a physical signature.", 105, 270, { align: "center" });

        // Signature line
        doc.line(150, 250, 190, 250);
        doc.text("Authorized Signature", 170, 255, { align: "center" });

        // Save the PDF locally for the user
        doc.save(`Receipt_${receiptData.student.rollNumber}_${receiptData.transactionId}.pdf`);

        try {
            // Convert PDF to base64 data for cloud storage
            const pdfData = doc.output('dataurlstring');

            // Upload to Cloudinary via your API
            const token = localStorage.getItem("token");
            if (!token) {
                toast.error("Unauthorized! Please log in again.");
                return;
            }

            // Show upload in progress toast
            const uploadingToast = toast.loading("Uploading receipt to cloud storage...");

            const response = await fetch("https://schoolpro-api.uniums.com/api/receipt/pdf-receipt", {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    "Authorization": `Bearer ${token}`
                },
                body: JSON.stringify({
                    pdfData: pdfData,
                    studentId: receiptData.student._id,
                    receiptId: receiptData.transactionId,
                    paymentDetails: {
                        amount: receiptData.total,
                        method: receiptData.paymentMethod,
                        feeTypes: receiptData.feeTypes.map(fee => ({
                            type: fee.type,
                            amount: fee.amount
                        })),
                        date: receiptData.date
                    },
                    folderName: "payment-receipt" // Specify the folder name for storage
                })
            });

            // Update the toast based on response
            toast.dismiss(uploadingToast);

            if (!response.ok) {
                const errorData = await response.json();
                throw new Error(errorData.message || "Failed to upload receipt to cloud");
            }

            const result = await response.json();
            toast.success("Receipt saved to cloud successfully!");

            // Show success message
            setPaymentSuccess(true);

        } catch (error) {
            console.error("Error saving receipt to cloud:", error);
            toast.error(`Error saving receipt to cloud: ${error.message}`);
        }

        // Reset form after successful payment (with a delay to show success message)
        setTimeout(() => {
            setPaymentSuccess(false);
            setSelectedStudent(null);
            setSelectedFeeTypes([]);
            setPaymentMethod('');
            setShowPreview(false);
            setReceiptData(null);
            setPaymentAmount({});
            setAccountDetails(null);
        }, 3000);
    };

    // Get student's hostel room information if available
    const getHostelRoom = (student) => {
        if (student.hostelRoom && student.hostelRoom.roomId) {
            return `Room assigned`;
        }
        return 'No room assigned';
    };

    return (
        <>
            <div className="payroll-container">
                <div className="payroll-header">
                    <h1>Fee Payment Management</h1>
                    <p>Process student payments and generate receipts</p>
                </div>

                {!paymentSuccess ? (
                    <>
                        {!showPreview ? (
                            <div className="payroll-content">
                                <div className="payroll-search-section">
                                    <div className="payroll-search-container">
                                        <div className="payroll-search-bar">
                                            <input
                                                type="text"
                                                placeholder="Search student by name or roll number..."
                                                value={searchQuery}
                                                onChange={(e) => setSearchQuery(e.target.value)}
                                            />
                                            <button className="payroll-search-button">
                                                <Search size={18} />
                                                Search
                                            </button>
                                        </div>

                                        {isLoading && (
                                            <>
                                                <Loader />
                                                <div className="payroll-loading">
                                                    Loading students...
                                                </div>
                                            </>

                                        )}

                                        {searchQuery && filteredStudents.length > 0 && !selectedStudent && !isLoading && (
                                            <div className="payroll-search-results">
                                                {filteredStudents.map(student => (
                                                    <div
                                                        key={student._id}
                                                        className="payroll-student-card"
                                                        onClick={() => setSelectedStudent(student)}
                                                    >
                                                        <div className="payroll-student-avatar">
                                                            <User size={24} />
                                                        </div>
                                                        <div className="payroll-student-info">
                                                            <h3>{student.firstName} {student.lastName}</h3>
                                                            <p>Roll: {student.rollNumber} | Grade: {student.grade}-{student.section}</p>
                                                        </div>
                                                    </div>
                                                ))}
                                            </div>
                                        )}

                                        {searchQuery && filteredStudents.length === 0 && !isLoading && (
                                            <div className="payroll-no-results">
                                                No students found. Please try another search term.
                                            </div>
                                        )}
                                    </div>
                                </div>

                                {selectedStudent && (
                                    <div className="payroll-payment-section">
                                        <div className="payroll-student-details">
                                            <div className="payroll-section-header">
                                                <h2>Student Details</h2>
                                                <button onClick={() => setSelectedStudent(null)} className="payroll-clear-button">
                                                    <X size={16} />
                                                    Clear
                                                </button>
                                            </div>
                                            <div className="payroll-student-profile">
                                                <div className="payroll-student-avatar-large">
                                                    <User size={40} />
                                                </div>
                                                <div className="payroll-student-info-detailed">
                                                    <h2>{selectedStudent.firstName} {selectedStudent.lastName}</h2>
                                                    <div className="payroll-student-metadata">
                                                        <div>
                                                            <strong>Roll Number:</strong> {selectedStudent.rollNumber}
                                                        </div>
                                                        <div>
                                                            <strong>Grade:</strong> {selectedStudent.grade} - {selectedStudent.section}
                                                        </div>
                                                        <div>
                                                            <strong>Email:</strong> {selectedStudent.email}
                                                        </div>
                                                        <div>
                                                            <strong>Phone:</strong> {selectedStudent.phone}
                                                        </div>
                                                        <div>
                                                            <strong>Parent:</strong> {selectedStudent.parentName} ({selectedStudent.parentPhone})
                                                        </div>
                                                        <div>
                                                            <strong>Hostel Room:</strong> {getHostelRoom(selectedStudent)}
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>

                                        <div className="payroll-fee-selection">
                                            <div className="payroll-section-header">
                                                <h2>Select Fee Types</h2>
                                            </div>
                                            <div className="payroll-fee-types">
                                                {feeTypes.map(fee => (
                                                    <div
                                                        key={fee.id}
                                                        className={`payroll-fee-card ${selectedFeeTypes.find(f => f.id === fee.id) ? 'payroll-selected' : ''}`}
                                                        onClick={() => handleFeeTypeSelect(fee)}
                                                    >
                                                        <div className="payroll-fee-type-name">{fee.type}</div>
                                                        <div className="payroll-fee-amount">
                                                            {selectedFeeTypes.find(f => f.id === fee.id) && (
                                                                <input
                                                                    type="number"
                                                                    value={paymentAmount[fee.id] || fee.defaultAmount}
                                                                    onChange={(e) => handleAmountChange(fee.id, e.target.value)}
                                                                />
                                                            )}
                                                            {!selectedFeeTypes.find(f => f.id === fee.id) && (
                                                                <span>₹{fee.defaultAmount}</span>
                                                            )}
                                                        </div>
                                                    </div>
                                                ))}
                                            </div>
                                        </div>

                                        {selectedFeeTypes.length > 0 && (
                                            <div className="payroll-payment-method">
                                                <div className="payroll-section-header">
                                                    <h2>Payment Method</h2>
                                                </div>
                                                <div className="payroll-payment-methods">
                                                    {paymentMethods.map(method => (
                                                        <div
                                                            key={method.id}
                                                            className={`payroll-payment-method-card ${paymentMethod === method.name ? 'payroll-selected' : ''}`}
                                                            onClick={() => handlePaymentMethodSelect(method)}
                                                        >
                                                            <CreditCard size={24} />
                                                            <span>{method.name}</span>
                                                        </div>
                                                    ))}
                                                </div>

                                                {/* Payment Method Account Details */}
                                                {accountDetails && (
                                                    <div className="payroll-account-details">
                                                        <h3>Payment Instructions</h3>
                                                        <div className="payroll-account-info">
                                                            {paymentMethod === 'UPI' && (
                                                                <>
                                                                    <div className="payroll-qr-code">
                                                                        <img src="/api/placeholder/200/200" alt="UPI QR Code" />
                                                                    </div>
                                                                    <div className="payroll-upi-details">
                                                                        <p><strong>UPI ID:</strong> {accountDetails.upiId}</p>
                                                                        <p>{accountDetails.instructions}</p>
                                                                    </div>
                                                                </>
                                                            )}

                                                            {paymentMethod === 'Bank Transfer' && (
                                                                <div className="payroll-bank-details">
                                                                    <p><strong>Account Number:</strong> {accountDetails.accountNumber}</p>
                                                                    <p><strong>IFSC Code:</strong> {accountDetails.ifscCode}</p>
                                                                    <p><strong>Account Name:</strong> {accountDetails.accountName}</p>
                                                                    <p><strong>Bank Name:</strong> {accountDetails.bankName}</p>
                                                                    <p><strong>Note:</strong> {accountDetails.instructions}</p>
                                                                </div>
                                                            )}

                                                            {paymentMethod === 'Cash' && (
                                                                <div className="payroll-cash-details">
                                                                    <p><strong>Payment Location:</strong> {accountDetails.location}</p>
                                                                    <p><strong>Timings:</strong> {accountDetails.timings}</p>
                                                                    <p><strong>Note:</strong> {accountDetails.instructions}</p>
                                                                </div>
                                                            )}
                                                        </div>
                                                    </div>
                                                )}
                                            </div>
                                        )}

                                        {selectedFeeTypes.length > 0 && paymentMethod && (
                                            <div className="payroll-payment-summary">
                                                <div className="payroll-section-header">
                                                    <h2>Payment Summary</h2>
                                                </div>
                                                <div className="payroll-summary-content">
                                                    <div className="payroll-summary-fees">
                                                        {selectedFeeTypes.map(fee => (
                                                            <div key={fee.id} className="payroll-summary-fee-item">
                                                                <span>{fee.type}</span>
                                                                <span>₹{paymentAmount[fee.id] || fee.defaultAmount}</span>
                                                            </div>
                                                        ))}
                                                    </div>
                                                    <div className="payroll-total">
                                                        <span>Total Amount</span>
                                                        <span>₹{calculateTotal()}</span>
                                                    </div>
                                                    <button onClick={handleGenerateReceipt} className="payroll-generate-receipt-button">
                                                        <Printer size={18} />
                                                        Generate Receipt
                                                    </button>
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                )}
                            </div>
                        ) : (
                            <div className="payroll-receipt-preview">
                                <div className="payroll-section-header">
                                    <h2>Receipt Preview</h2>
                                    <button onClick={() => setShowPreview(false)} className="payroll-back-button">
                                        Back to Payment
                                    </button>
                                </div>

                                <div className="payroll-receipt">
                                    <div className="payroll-receipt-header">
                                        <div className="payroll-institution-logo">
                                            <div className="payroll-logo-placeholder">
                                                <img src={Logo} alt="Institution Logo" />
                                            </div>
                                        </div>
                                        <div className="payroll-institution-details">
                                            <h2>UniUms School Pro</h2>
                                            <p>Address Betnoti, Mayurbhanj, Address Line 2</p>
                                            <p>Phone: 123-456-7890 | Email: info@institution.edu</p>
                                        </div>
                                    </div>

                                    <div className="payroll-receipt-title">
                                        <h2>PAYMENT RECEIPT</h2>
                                        <div className="payroll-receipt-id">
                                            <p>Receipt No: {receiptData?.transactionId}</p>
                                            <p>Date: {receiptData?.date}</p>
                                        </div>
                                    </div>

                                    <div className="payroll-receipt-student">
                                        <h3>Student Details</h3>
                                        <div className="payroll-receipt-student-info">
                                            <div>
                                                <p><strong>Name:</strong> {receiptData?.student.firstName} {receiptData?.student.lastName}</p>
                                                <p><strong>Roll No:</strong> {receiptData?.student.rollNumber}</p>
                                            </div>
                                            <div>
                                                <p><strong>Grade:</strong> {receiptData?.student.grade}-{receiptData?.student.section}</p>
                                                <p><strong>Parent:</strong> {receiptData?.student.parentName}</p>
                                            </div>
                                        </div>
                                    </div>

                                    <div className="payroll-receipt-fees">
                                        <h3>Fee Details</h3>
                                        <table className="payroll-receipt-table">
                                            <thead>
                                                <tr>
                                                    <th>No.</th>
                                                    <th>Description</th>
                                                    <th>Amount (₹)</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {receiptData?.feeTypes.map((fee, index) => (
                                                    <tr key={fee.id}>
                                                        <td>{index + 1}</td>
                                                        <td>{fee.type}</td>
                                                        <td>{fee.amount}</td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                            <tfoot>
                                                <tr>
                                                    <td colSpan="2">Total</td>
                                                    <td>₹{receiptData?.total}</td>
                                                </tr>
                                            </tfoot>
                                        </table>
                                    </div>

                                    <div className="payroll-receipt-payment">
                                        <h3>Payment Details</h3>
                                        <p><strong>Payment Method:</strong> {receiptData?.paymentMethod}</p>
                                        <p><strong>Transaction ID:</strong> {receiptData?.transactionId}</p>
                                        <p><strong>Payment Status:</strong> <span className="payroll-status-success">Success</span></p>
                                    </div>

                                    <div className="payroll-receipt-footer">
                                        <div className="payroll-authorized-signature">
                                            <div className="payroll-signature-line"></div>
                                            <p>Authorized Signature</p>
                                        </div>

                                        <div className="payroll-receipt-notes">
                                            <p>This is a computer-generated receipt and does not require a physical signature.</p>
                                        </div>
                                    </div>
                                </div>

                                <div className="payroll-receipt-actions">
                                    <button onClick={handleDownloadReceipt} className="payroll-download-button">
                                        <Download size={18} />
                                        Download & Save Receipt
                                    </button>
                                </div>
                            </div>
                        )}
                    </>
                ) : (
                    <div className="payroll-success-message">
                        <div className="payroll-success-icon">
                            <CheckCircle size={48} />
                        </div>
                        <h2>Payment Successful!</h2>
                        <p>Receipt has been downloaded and saved to cloud storage.</p>
                    </div>
                )}
            </div>
            <ToastContainer />
        </>

    );
}

export default Payment;